import React from "react";
import Plot from "react-plotly.js";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Config, Layout, PlotData } from "plotly.js";

type Props = {
    data: number[][];
    vesselAge: number;
    vesselGrade?: number;
    vesselGradeRange?: string;
    ageRange: string;
};

const mapColor = (age: number, ageRangeValues: number[]) => {
    if (age >= ageRangeValues[0] && age <= ageRangeValues[1]) {
        return "#00C7CC";
    }

    return "#BBBFC3";
};

export const GradeScatterChart = (props: Props) => {
    const { data, vesselGrade, vesselAge, vesselGradeRange, ageRange } = props;

    const { t } = useTranslation("common");

    const ageRangeValues = useMemo(
        () => ageRange.split("-").map((age) => Number(age)),
        [ageRange]
    );

    let averageGrade = 0;

    const plotData = useMemo(() => {
        const x: number[] = [];
        const y: number[] = [];
        let sum = 0;

        data.forEach(([grade, age]) => {
            sum += grade;

            x.push(age);
            y.push(grade);
        });

        averageGrade = Math.ceil(sum / data.length);

        return {
            x: x,
            y: y,
            type: "scatter",
            mode: "markers",
            marker: {
                size: 13,
                opacity: 0.5,
                color: x.map((age) => mapColor(age, ageRangeValues))
            }
        } as PlotData;
    }, [data, ageRange]);

    const layout = useMemo(() => {
        const graphLayout: Partial<Layout> = {
            autosize: true,
            xaxis: {
                showgrid: false,
                zerolinecolor: "#B0E2E4",
                tickfont: {
                    color: "#777",
                    size: 13
                },
                title: t("inspectionReport.gradeComparison.shipAge"),
                titlefont: {
                    color: "#555",
                    size: 13
                }
            },
            yaxis: {
                showgrid: true,
                automargin: true,
                gridcolor: "#B0E2E4",
                zerolinecolor: "#B0E2E4",
                tickfont: {
                    color: "#777",
                    size: 13
                }
            },
            font: {
                family: "Open Sans",
                color: "#555",
                size: 12
            },
            margin: {
                t: 0,
                l: 0,
                r: 0
            },
            annotations: [
                {
                    text: t("inspectionReport.gradeComparison.idwalGrade"),
                    font: {
                        color: "#555",
                        size: 13
                    },
                    showarrow: false,
                    xref: "paper",
                    yref: "paper",
                    x: 0,
                    y: 0.5,
                    xanchor: "left",
                    textangle: "270"
                },
                {
                    text: `${t(
                        "inspectionReport.gradeComparison.averageGrade"
                    )}: ${averageGrade}`,
                    xref: "paper",
                    yref: "y",
                    y: averageGrade + 2,
                    font: {
                        color: "#28235B",
                        size: 13
                    },
                    showarrow: false,
                    x: 0.6,
                    xanchor: "left"
                }
            ],
            shapes: [
                {
                    type: "line",
                    xref: "paper",
                    yref: "y",
                    x0: 0,
                    x1: 1,
                    y0: averageGrade,
                    y1: averageGrade,
                    line: {
                        color: "#28235B",
                        width: 2,
                        dash: "dash"
                    }
                }
            ],
            showlegend: false,
            plot_bgcolor: "#F4F8FB",
            height: 450,
            hoverlabel: {
                bgcolor: "#1C1940",
                bordercolor: "#1C1940",
                font: {
                    color: "#FFF",
                    size: 13
                }
            }
        };

        if (vesselGradeRange) {
            const [min, max] = vesselGradeRange.split("-");

            graphLayout.annotations?.push({
                text: `${t(
                    "inspectionReport.gradeComparison.estimatedGrade"
                )}: ${vesselGradeRange}`,
                x: vesselAge + 0.5,
                xref: "x",
                yref: "y",
                font: {
                    color: "#28235B",
                    size: 13
                },
                showarrow: false,
                y: vesselGrade,
                xanchor: "left"
            });

            graphLayout.shapes?.push({
                type: "line",
                xref: "x",
                yref: "y",
                xanchor: vesselAge,
                yanchor: vesselGrade,
                x0: vesselAge,
                x1: vesselAge,
                y0: Number(min),
                y1: Number(max),
                line: {
                    width: 18,
                    color: "#28235B"
                }
            });

            graphLayout.shapes?.push({
                type: "circle",
                xref: "x",
                yref: "y",
                xsizemode: "pixel",
                ysizemode: "pixel",
                xanchor: vesselAge,
                yanchor: min,
                x0: -8,
                x1: 8,
                y0: -8,
                y1: 8,
                fillcolor: "#28235B",
                line: {
                    color: "#28235B"
                }
            });

            graphLayout.shapes?.push({
                type: "circle",
                xref: "x",
                yref: "y",
                xsizemode: "pixel",
                ysizemode: "pixel",
                xanchor: vesselAge,
                yanchor: max,
                x0: -8,
                x1: 8,
                y0: -8,
                y1: 8,
                fillcolor: "#28235B",
                line: {
                    color: "#28235B"
                }
            });
        } else if (vesselGrade) {
            graphLayout.annotations?.push({
                text: `${t(
                    "inspectionReport.gradeComparison.vesselGrade"
                )}: ${vesselGrade}`,
                x: vesselAge + 0.5,
                xref: "x",
                yref: "y",
                font: {
                    color: "#28235B",
                    size: 13
                },
                showarrow: false,
                y: vesselGrade,
                xanchor: "left"
            });

            graphLayout.shapes?.push({
                type: "circle",
                xref: "x",
                yref: "y",
                xsizemode: "pixel",
                ysizemode: "pixel",
                xanchor: vesselAge,
                yanchor: vesselGrade,
                x0: -8,
                x1: 8,
                y0: -8,
                y1: 8,
                fillcolor: "#28235B",
                line: {
                    color: "#28235B"
                }
            });
        }

        return graphLayout;
    }, [data]);

    const config = useMemo(() => {
        const graphConfig: Partial<Config> = {
            displayModeBar: false,
            responsive: true,
            displaylogo: false,
            scrollZoom: false,
            showTips: false
        };

        return graphConfig;
    }, []);

    return (
        <div className="col-12 lg:col-8">
            <Plot
                className="grade-comparison-chart flex"
                useResizeHandler={true}
                data={[plotData]}
                layout={layout}
                config={config}
            />
        </div>
    );
};
